/**
 * Lista dos últimos 3 feedbacks de treino do aluno (intensidade,
 * fadiga, dor), com link "Ver todos →" pra tela completa.
 *
 * Cada feedback vira uma linha com 3 badges (emoji + número):
 *   - Intensidade (😴/🙂/💪/🔥/🤯) — verde até 3, vermelho 4-5
 *   - Fadiga (😊/🙂/😓/😩/💀) — verde até 3, vermelho 4-5
 *   - Dor (✅ sem dor / ⚠️ local da dor) — rosa se com dor
 * Quando o aluno deixou um recado (observations), ele aparece em itálico
 * numa linha abaixo dos badges.
 *
 * Props:
 *   - studentId: id do aluno
 *   - studentName: nome (passado ao push da rota "ver todos")
 *
 * Estado interno: usa `useFeedbackHistory(studentId, 3)` — fetcha 3
 * últimos feedbacks. Esse hook usa Supabase + RLS, então só funciona
 * com personal autenticado dono do aluno.
 *
 * Quando NÃO usar: pra outra entidade que não seja "treino do aluno",
 * o hook não serve — crie próprio.
 */

import { memo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';
import { useFeedbackHistory } from '@/hooks/useFeedbackHistory';
import { sharedStyles } from './styles';

interface Props {
  studentId: string;
  studentName: string;
}

const INTENSITY_EMOJI = ['😴', '🙂', '💪', '🔥', '🤯'];
const FATIGUE_EMOJI = ['😊', '🙂', '😓', '😩', '💀'];

function FeedbackSectionImpl({ studentId, studentName }: Props) {
  const { feedbacks } = useFeedbackHistory(studentId, 3);
  const router = useRouter();

  return (
    <View style={sharedStyles.section}>
      <View style={sharedStyles.sectionRow}>
        <Text style={sharedStyles.sectionTitle}>Feedbacks</Text>
        {feedbacks.length > 0 && (
          <TouchableOpacity
            onPress={() => router.push({
              pathname: '/(personal)/feedback/[studentId]',
              params: { studentId, name: studentName },
            } as any)}
          >
            <Text style={sharedStyles.linkText}>Ver todos →</Text>
          </TouchableOpacity>
        )}
      </View>

      {feedbacks.length === 0 ? (
        <Text style={sharedStyles.empty}>Nenhum feedback registrado ainda.</Text>
      ) : (
        <View style={styles.feedbackList}>
          {feedbacks.map((item, index) => (
            <View
              key={item.id}
              style={[styles.feedbackItem, index < feedbacks.length - 1 && styles.feedbackRowBorder]}
            >
              <View style={styles.feedbackRow}>
                <View>
                  <Text style={styles.feedbackPlanName}>{item.planName}</Text>
                  <Text style={styles.feedbackDate}>
                    {new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                  </Text>
                </View>
                <View style={styles.feedbackBadges}>
                  <View style={[styles.badge, item.intensity >= 4 ? styles.badgeHigh : styles.badgeLow]}>
                    <Text style={[styles.badgeText, item.intensity >= 4 ? styles.badgeTextHigh : styles.badgeTextLow]}>
                      {INTENSITY_EMOJI[item.intensity - 1]}{item.intensity}
                    </Text>
                  </View>
                  <View style={[styles.badge, item.fatigue >= 4 ? styles.badgeHigh : styles.badgeLow]}>
                    <Text style={[styles.badgeText, item.fatigue >= 4 ? styles.badgeTextHigh : styles.badgeTextLow]}>
                      {FATIGUE_EMOJI[item.fatigue - 1]}{item.fatigue}
                    </Text>
                  </View>
                  <View style={[styles.badge, item.hadPain ? styles.badgePain : styles.badgeLow]}>
                    <Text style={[styles.badgeText, item.hadPain ? styles.badgeTextPain : styles.badgeTextLow]}>
                      {item.hadPain ? `⚠️ ${item.painLocation ?? 'Sim'}` : '✅ sem dor'}
                    </Text>
                  </View>
                </View>
              </View>
              {item.observations ? (
                <Text style={styles.feedbackObs}>{`"${item.observations}"`}</Text>
              ) : null}
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  feedbackList: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#eee',
    overflow: 'hidden',
    backgroundColor: '#fafafa',
  },
  feedbackItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#fafafa',
  },
  feedbackRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  feedbackRowBorder: { borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  feedbackObs: { fontSize: 11, color: '#555', fontStyle: 'italic', marginTop: 6 },
  feedbackPlanName: { fontSize: 12, fontWeight: '600', color: '#1A1A1A' },
  feedbackDate: { fontSize: 10, color: '#aaa', marginTop: 2 },
  feedbackBadges: { flexDirection: 'row', gap: 4, alignItems: 'center' },
  badge: { borderRadius: 6, paddingHorizontal: 6, paddingVertical: 2 },
  badgeText: { fontSize: 10, fontWeight: '600' },
  badgeHigh: { backgroundColor: '#FFF0F2' },
  badgeTextHigh: { color: '#C8102E' },
  badgeLow: { backgroundColor: '#f1f8e9' },
  badgeTextLow: { color: '#388e3c' },
  badgePain: { backgroundColor: '#fce4ec' },
  badgeTextPain: { color: '#c62828' },
});

export const FeedbackSection = memo(FeedbackSectionImpl);
